import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9, default as _styled10 } from "styled-components";
import { hasItems } from "~/features/app/utils";
import { Wrap } from "~/features/ui/wrap";
import { Grid } from "~/features/ui/grid";
import { Headline } from "~/features/ui/headline";
import { RichText } from "~/features/ui/rich-text";
import { getThemeStyle, type Mode, type Theme } from "~/features/ui/get-theme-style";
import { Button } from "~/features/ui/button";
import { Link } from "~/features/ui/link";
import ArrowRight from "~/public/icons/arrow-right.svg";
type SectionDataAttrs = Record<`data-${string}`, string | undefined>;
export type IntroDetailsItemView = {
  label?: string | null;
  text?: string | null;
  baseLink?: string | null;
  /** Multiple values in a row, such as products or industries. */
  values?: Array<{
    text?: string | null;
    href?: string | null;
  }> | null;
};
function IntroDetailsItemsView({
  details
}: {
  details?: IntroDetailsItemView[] | null;
}) {
  const themeStyle = getThemeStyle({
    mode: "light"
  });
  if (!hasItems(details)) {
    return null;
  }
  return <div>
      {details.map((detail, idx) => <_StyledDiv key={idx} $_css={[{
      "display": "flex",
      "flexDirection": "column",
      "alignItems": "flex-start",
      "rowGap": "0.8rem",
      "borderBottomWidth": "1px",
      "paddingBottom": "1.6rem",
      "&:not(:first-child)": {
        "paddingTop": "1.6rem"
      },
      "@media (min-width: 1024px)": {
        "paddingBottom": "2.4rem",
        "&:not(:first-child)": {
          "paddingTop": "2.4rem"
        }
      }
    }, themeStyle.border]}>
          <_StyledP>{detail.label}</_StyledP>
          {hasItems(detail.values) ? <_StyledDiv2>
              {detail.values.map((value, valueIdx) => value.href ? <Button as={Link} href={value.href} variant="underline" icon={ArrowRight} key={valueIdx}>
                    {value.text}
                  </Button> : <p key={valueIdx}>{value.text}</p>)}
            </_StyledDiv2> : detail.baseLink ? <Button as={Link} href={detail.baseLink} variant="underline" icon={ArrowRight}>
              {detail.text}
            </Button> : <p>{detail.text}</p>}
        </_StyledDiv>)}
    </div>;
}
export type IntroDetailsBlockViewProps = {
  anchor?: Array<{
    id?: string | null;
    name?: string | null;
  }> | {
    id?: string | null;
    name?: string | null;
  } | null;
  details?: IntroDetailsItemView[] | null;
  headline?: string | null;
  mode?: string | null;
  sectionProps?: React.ComponentPropsWithoutRef<"section"> & SectionDataAttrs;
  text?: React.ReactNode;
  theme?: string | null;
};
export function IntroDetailsBlockView({
  details,
  headline,
  mode = "light",
  sectionProps,
  text,
  theme = "white"
}: IntroDetailsBlockViewProps) {
  const themeStyle = getThemeStyle({
    mode: mode as Mode,
    theme: theme as Theme
  });
  if (!hasItems(details)) {
    return null;
  }
  return <_StyledSection {...sectionProps} $_css2={[themeStyle.text, themeStyle.border, themeStyle.background]}>
      <Wrap mode={mode as Mode}>
        <_StyledDiv3>
          <_StyledGrid $_css3={[{
          "position": "relative",
          "zIndex": "10",
          "@media (min-width: 1024px)": {
            "borderLeftWidth": "1px",
            "borderRightWidth": "1px"
          }
        }, themeStyle.background, themeStyle.border]}>
            <_StyledDiv4 $_css4={[{
            "gridColumn": "1 / -1",
            "borderBottomWidth": "1px",
            "padding": "4rem",
            "@media (min-width: 1024px)": {
              "gridColumn": "span 4 / span 4",
              "borderBottomWidth": "0px",
              "borderRightWidth": "1px"
            }
          }, themeStyle.border]}>
              <IntroDetailsItemsView details={details} />
            </_StyledDiv4>
            <_StyledDiv5 $_css5={[{
            "gridColumn": "1 / -1",
            "padding": "4rem",
            "@media (min-width: 1024px)": {
              "gridColumn": "span 8 / span 8",
              "display": "flex",
              "alignItems": "center",
              "padding": "8rem"
            }
          }]}>
              <_StyledDiv6>
                {headline && <_StyledHeadline type="h2" text={headline} />}
                {text}
              </_StyledDiv6>
            </_StyledDiv5>
          </_StyledGrid>
        </_StyledDiv3>
      </Wrap>
    </_StyledSection>;
}
var _StyledDiv = /*#__PURE__*/_styled("div").withConfig({
  componentId: "sc-sf-1tq44nb-0"
})`${p => p.$_css}`;
var _StyledP = _styled2("p").withConfig({
  componentId: "sc-sf-1tq44nb-1"
})({
  "fontSize": "1.2rem",
  "lineHeight": "1.1",
  "letterSpacing": "0.024rem",
  "fontWeight": "400",
  "--tw-text-opacity": "1",
  "color": "rgb(108 112 97 / var(--tw-text-opacity))",
  "@media (min-width: 1024px)": {
    "fontSize": "1.4rem",
    "lineHeight": "1.1",
    "letterSpacing": "0.028000000000000004rem",
    "fontWeight": "400"
  }
});
var _StyledDiv2 = _styled3("div").withConfig({
  componentId: "sc-sf-1tq44nb-2"
})({
  "display": "flex",
  "flexWrap": "wrap",
  "alignItems": "center",
  "gap": "1.6rem"
});
var _StyledSection = _styled4("section").withConfig({
  componentId: "sc-sf-1tq44nb-3"
})`${p => p.$_css2}`;
var _StyledDiv3 = _styled5("div").withConfig({
  componentId: "sc-sf-1tq44nb-4"
})({
  "@media (min-width: 1024px)": {
    "paddingLeft": "4rem",
    "paddingRight": "4rem"
  }
});
var _StyledGrid = _styled6(Grid).withConfig({
  componentId: "sc-sf-1tq44nb-5"
})`${p => p.$_css3}`;
var _StyledDiv4 = _styled7("div").withConfig({
  componentId: "sc-sf-1tq44nb-6"
})`${p => p.$_css4}`;
var _StyledDiv5 = _styled8("div").withConfig({
  componentId: "sc-sf-1tq44nb-7"
})`${p => p.$_css5}`;
var _StyledDiv6 = _styled9("div").withConfig({
  componentId: "sc-sf-1tq44nb-8"
})({
  "display": "flex",
  "flexDirection": "column",
  "rowGap": "4rem",
  "@media (min-width: 1024px)": {
    "justifyContent": "center",
    "paddingTop": "4rem",
    "paddingBottom": "4rem"
  }
});
var _StyledHeadline = /*#__PURE__*/_styled10(Headline).withConfig({
  displayName: "intro-details___StyledHeadline",
  componentId: "sc-1m3170l-0"
})({
  "fontSize": "2.4rem",
  "lineHeight": "1.2",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "3.8rem",
    "lineHeight": "1.3",
    "letterSpacing": "0.038rem",
    "fontWeight": "400"
  }
});